import React, { Component, Fragment } from 'react';
import { inject, observer } from 'mobx-react';
import PropTypes from 'prop-types';
import b_ from 'b_';
import BrowserPoliciesStore from '../../stores/BrowserPolicies/BrowserPoliciesStore';
import SnackStore from '../../stores/SnackStore';
import FuseConfirmationModal from '../ConfirmationModal/FuseConfirmationModal';
import { ConsoleCard, ConsoleCardTitle, ConsoleField } from '../ConsoleFragments';
import ConsoleModal from '../ConsoleModal/ConsoleModal';
import Button from '../Button';
import Accordion from '../Accordion/Accordion';
import DataLossPrevention from '../DataLossPrevention/DataLossPrevention';
import { ObservableToggle } from '../Toggle/Toggle';
import { ObservableTextfield } from '../Textfield';
import ContentFiltering from '../ContentFiltering/ContentFiltering';
import './BrowserPolicyModal.css';

const block = b_.lock('BrowserPolicyModal');

const getPolicyDisplayName = (p) => {
    if (p && p.isDefault) {
        return 'Default Policy';
    }
    if (!p.name) {
        return 'Policy';
    }
    return `Policy “${p.name}”`;
};

class BrowserPolicyModal extends Component {
    static propTypes = {
        browserPolicies: BrowserPoliciesStore.PropType.isRequired,
        snack: SnackStore.PropType.isRequired,
        hideDefinitionSection: PropTypes.bool,
        hideUiSection: PropTypes.bool,
        hideSecuritySection: PropTypes.bool,
        hideDlpSection: PropTypes.bool,
        hideFilteringSection: PropTypes.bool,
    };


    onCreate = (event) => {
        event.preventDefault();
        this.props.browserPolicies.createPolicy()
            .then((p) => p && this.props.snack.showSuccess(`${getPolicyDisplayName(p)} has been added successfully.`))
            .catch((e) => e && this.props.snack.showError(`Failed to add policy. ${e.message}`));
    };

    onUpdate = (event) => {
        event.preventDefault();
        this.props.browserPolicies.updatePolicy()
            .then((p) => p && this.props.snack.showSuccess(`${getPolicyDisplayName(p)} has been updated successfully.`))
            .catch((e) => e && this.props.snack.showError(`Failed to update policy. ${e.message}`));
    };

    onCloseClick = () => {
        const { editStore } = this.props.browserPolicies;
        if (editStore.hasChanged() && !editStore.viewMode) {
            editStore.exitConfirmationModal.open();
        } else {
            editStore.modal.close();
        }
    };

    getNameErrorMessage = () => {
        const { nameStore, isDefault } = this.props.browserPolicies.editStore;
        if (!isDefault && !nameStore.isTouched && nameStore.isEmpty) {
            return 'Policy name is required';
        }
    };

    renderSections = () => {
        const { editStore } = this.props.browserPolicies;
        const { flags } = editStore.uiSettings;

        return <Fragment>
            {this.props.hideDefinitionSection ? null : <Accordion
                label="Policy Definition"
                labelClassName={block('AccordionLabel')}
                defaultExpanded
            >
                {editStore.isDefault ? <div className={block('PolicyNameField')}>
                    <div className={block('DefaultPolicyFieldName')}>Policy Name</div>
                    <div className={block('DefaultPolicyFieldValue')}>
                        <strong>Default Policy</strong>
                    </div>
                </div> : <ObservableTextfield
                    className={block('PolicyNameField')}
                    label="Policy Name"
                    maxLength="50"
                    store={editStore.nameStore}
                    errorLabel={this.getNameErrorMessage()}
                />}
            </Accordion>}

            {this.props.hideUiSection ? null : <Accordion
                label="User Interface Options"
                labelClassName={block('AccordionLabel')}
                defaultExpanded
            >
                <ConsoleCard>
                    <ConsoleCardTitle>MENU ITEMS</ConsoleCardTitle>
                    <div className={block('SectionContentArrange')}>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Print"
                            info="Display “Print” option in menu."
                            infoPosition="right"
                        >
                            <ObservableToggle
                                onLabel={'SHOW'}
                                offLabel={'HIDE'}
                                store={flags.printEnabled}
                            />
                        </ConsoleField>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Clear Site Cookies"
                            info="Display “Clear Site Cookies” option in menu.
                                  This allows the user to delete all saved cookies."
                        >
                            <ObservableToggle
                                onLabel={'SHOW'}
                                offLabel={'HIDE'}
                                store={flags.clearCookiesEnabled}
                            />
                        </ConsoleField>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Feedback"
                            info="Display “Proofpoint Feedback” option in menu.
                                  This allows the user to send feedback to the Proofpoint support team."
                        >
                            <ObservableToggle
                                onLabel={'SHOW'}
                                offLabel={'HIDE'}
                                store={flags.feedbackEnabled}
                            />
                        </ConsoleField>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="FAQ & Help"
                            info="Display “FAQ & Help” option in menu.
                                  This allows the user to see the online help and frequently asked questions."
                        >
                            <ObservableToggle
                                onLabel={'SHOW'}
                                offLabel={'HIDE'}
                                store={flags.faqEnabled}
                            />
                        </ConsoleField>
                    </div>
                </ConsoleCard>

                <ConsoleCard>
                    <ConsoleCardTitle>SETTINGS PAGE</ConsoleCardTitle>
                    <div className={block('SectionContentArrange')}>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Research Mode"
                            info="Display “Research Mode” option on settings page. This option allows users
                                  to change the browsing location and impersonate other browsers (user agent string)."
                            infoPosition="right"
                        >
                            <ObservableToggle
                                onLabel={'SHOW'}
                                offLabel={'HIDE'}
                                store={flags.settingsResearchMode}
                            />
                        </ConsoleField>
                    </div>
                </ConsoleCard>

                <ConsoleCard>
                    <ConsoleCardTitle>BROWSER BAR</ConsoleCardTitle>
                    <div className={block('SectionContentArrange')}>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Ad Blocker"
                            info="Display “Ad Blocker” option on the browser bar.
                                  This option allows users to allow or block ads."
                            infoPosition="right"
                        >
                            <ObservableToggle
                                onLabel={'SHOW'}
                                offLabel={'HIDE'}
                                store={flags.adBlockSwitchEnabled}
                            />
                        </ConsoleField>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Block Ads by Default"
                            info="Set the initial state of the “Ad Blocker” to ON or OFF."
                        >
                            <ObservableToggle
                                onLabel={'ON'}
                                offLabel={'OFF'}
                                store={flags.blockAdsByDefault}
                            />
                        </ConsoleField>
                    </div>
                </ConsoleCard>

            </Accordion>}

            {this.props.hideSecuritySection ? null : <Accordion
                label="Security Options"
                labelClassName={block('AccordionLabel')}
                defaultExpanded
            >
                <ConsoleCard>
                    <ConsoleCardTitle>MALWARE AND PHISHING</ConsoleCardTitle>
                    <div className={block('SectionContentArrange')}>
                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Reputation Check"
                            info="Check the current URL and domain reputation against
                                  Proofpoint Threat Intelligence data to determine if
                                  it should be blocked due to an emerging or existing threat."
                            infoPosition="right"
                        >
                            <ObservableToggle
                                onLabel={'ENABLED'}
                                offLabel={'DISABLED'}
                                store={editStore.securityOptions.maliciousThreatsStore}
                            />
                        </ConsoleField>

                        <ConsoleField
                            className={block('ConsoleField')}
                            name="Threat Scan"
                            info="Check the contents of the URL destination, Document Object Model (DOM)
                                  or file, in real-time to determine if there is a Phishing or Malware
                                  threat present using Proofpoint Advanced Threat Protection."
                            infoPosition="right"
                        >
                            <ObservableToggle
                                onLabel={'ENABLED'}
                                offLabel={'DISABLED'}
                                store={editStore.securityOptions.tsaScanStore}
                            />
                        </ConsoleField>
                    </div>
                </ConsoleCard>
            </Accordion>}

            {this.props.hideDlpSection ? null : <Accordion
                label={'Data Loss Prevention'}
                labelClassName={block('AccordionLabel')}
                defaultExpanded
            >
                <DataLossPrevention dlp={editStore.dlp} />
            </Accordion>}

            {this.props.hideFilteringSection ? null : <Accordion
                label={'Content Filtering'}
                labelClassName={block('AccordionLabel')}
                defaultExpanded
            >
                <ContentFiltering contentFilteringStore={editStore.contentFiltering} />
            </Accordion>}
        </Fragment>;
    };

    getModalTitle(editStore) {
        if (editStore.isNew) {
            return 'Add New Policy';
        }
        if (editStore.viewMode) {
            return 'View Policy';
        }
        return 'Edit Policy';
    }

    render() {
        const { editStore, updateStatus, createStatus } = this.props.browserPolicies;

        return <Fragment>
            <ConsoleModal
                isOpen={editStore.modal.isOpen}
                onClose={this.onCloseClick}
                onAfterOpen={() => editStore.isNew && editStore.nameStore.focus()}
                title={this.getModalTitle(editStore)}
                style={{ content: { width: '100%', maxWidth: 1010 } }}
                contentClassName={block('ModalContent')}
                headerActions={editStore.isNew ? <Button
                    style={{ minWidth: 100 }}
                    primaryGreen
                    raised
                    disabled={!editStore.isValid}
                    loading={createStatus.isLoading}
                    onClick={this.onCreate}
                >Add</Button> : <Button
                    style={{ minWidth: 100 }}
                    primaryGreen
                    raised
                    disabled={!editStore.hasChanged() || !editStore.isValid || editStore.viewMode}
                    loading={updateStatus.isLoading}
                    onClick={this.onUpdate}
                >Save{editStore.viewMode ? ' - Disabled' : ''}</Button>}
            >{this.renderSections()}</ConsoleModal>

            <FuseConfirmationModal
                store={editStore.exitConfirmationModal}
                onConfirm={editStore.modal.close}
            />
        </Fragment>;
    }
}

export default inject('browserPolicies', 'snack')(observer(BrowserPolicyModal));
